import {
  hidesCardTitle,
  legacyTiptapToMarkdown,
  notePreviewLines,
  HASHTAG_BODY_PATTERN,
} from '@caliu/utils'

/** A hashtag token (`#tag` or `#multi word#`), from the shared cross-platform grammar. */
const HASHTAG_STRIP_RE = new RegExp(`#${HASHTAG_BODY_PATTERN}`, 'gu')

type JsonNode = { type: string; text?: string; content?: JsonNode[] }

function extractTextFromJson(node: JsonNode): string {
  if (node.type === 'text') return node.text ?? ''
  if (!node.content) return ''
  return node.content.map(extractTextFromJson).join(' ')
}

function parseTiptapDoc(content: string): JsonNode[] | null {
  if (!content.trimStart().startsWith('{')) return null
  try {
    const doc = JSON.parse(content)
    if (doc && typeof doc === 'object' && doc.type === 'doc' && Array.isArray(doc.content)) {
      return doc.content as JsonNode[]
    }
  } catch {
    // fall through
  }
  return null
}

/**
 * The preview with its line structure kept, one line per body line, like the
 * native list rows (`NoteRowView.previewAttributedString`). Rendered with
 * `white-space: pre-line` and a line clamp. Legacy TipTap JSON is read as the
 * markdown it converts to, like Swift and Kotlin. `keepTitle` keeps the title
 * line, for a card that shows no title row (see `noteCardText`).
 */
export function getNotePreviewLines(
  content: string,
  { keepTitle = false }: { keepTitle?: boolean } = {},
): string {
  if (!content) return ''
  // Bullets read "• item" and numbers stay, like the native rows.
  return notePreviewLines(legacyTiptapToMarkdown(content), {
    lineLimit: 100,
    stripInline: true,
    keepTitle,
    listMarkers: true,
  })
    .map((line) => line.replace(HASHTAG_STRIP_RE, '').replace(/\s{2,}/g, ' ').trim())
    .filter(Boolean)
    .join('\n')
}

/** What a note card shows: its title row or not, and the preview under it. */
export interface NoteCardText {
  showsTitle: boolean
  preview: string
}

/**
 * A note whose title line is body text (`hidesCardTitle`, the rule Swift and
 * Kotlin cards share) shows no title row: its preview starts at line 1. Any
 * other note keeps its title row and a preview without that line, as does one
 * whose kept preview is empty. Callers with no preview (density "small")
 * always show the title.
 */
export function noteCardText(content: string): NoteCardText {
  if (hidesCardTitle(content)) {
    const kept = getNotePreviewLines(content, { keepTitle: true })
    if (kept) return { showsTitle: false, preview: kept }
  }
  return { showsTitle: true, preview: getNotePreviewLines(content) }
}

/** Extract a plain-text preview from note content (skips the title node). */
export function getNotePreview(content: string): string {
  if (!content) return ''

  const nodes = parseTiptapDoc(content)
  if (nodes) {
    const bodyNodes = nodes.slice(1)
    if (bodyNodes.length === 0) return ''
    return bodyNodes.map(extractTextFromJson).join(' ').replace(/\s{2,}/g, ' ').trim()
  }

  // Markdown path: shared with worker (push notifications) and Swift (note
  // row previews). Mirrors `previewMarkdownLines` in CaliuCore so all three
  // clients pick the same lines from the same content. Joined with a space
  // since note rows render in a single line-clamped container.
  //
  // No local unescape pass: `stripInline: true` already runs the shared
  // `unescapeMarkdown` inside the pipeline. A second pass here would unescape
  // twice, eating the surviving `\` that an escaped backslash (`\\`) produces.
  const lines = notePreviewLines(content, { lineLimit: 100, stripInline: true })
  return lines
    .join(' ')
    .replace(HASHTAG_STRIP_RE, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

/** A card preview's first line, which stands in for a hidden title. */
export function firstLine(text: string): string {
  const end = text.indexOf('\n')
  return end < 0 ? text : text.slice(0, end)
}

/** Everything after a card preview's first line, its line break included. */
export function restLines(text: string): string {
  const end = text.indexOf('\n')
  return end < 0 ? '' : text.slice(end)
}
